import { AxiosPromise, AxiosResponse } from "axios";
import axiosCustom from "@/utils/axiosCustom";
import store from "@/store/index";
import User from "@/models/User";
import IUser from "@/models/protocols/IUser";
import IAddress from "@/models/protocols/IAddress";
import Cookies from "cookies-js";
import { BillingInfo } from "@/types/BillingInfo";
import router from "@/router/index";
import IRegion from "@/models/protocols/IRegion";
import { Cart } from "@/types/Cart";

export default class UserService {
  private static USER_KEY: string = "p_user";

  /**
   * Get the system token for REST communication.
   * @param userName
   * @param password
   * @returns
   */
  static async token(userName: string, password: string): Promise<string> {
    const body = {
      username: userName,
      password: password,
    };
    const { data } = await axiosCustom.post(
      "/rest/V1/integration/customer/token",
      body
    );
    return data;
  }

  /**
   * Logs in the user into the system and gets the user info and puts it into store.
   * @param userName
   * @param password
   */
  // Get the Magento token first
  static async login(userName: string, password: string): Promise<boolean> {
    const token = await this.token(userName, password);
    const config = {
      headers: {
        Authorization: "Bearer " + token,
      },
    };

    const { data } = await axiosCustom.get("/rest/V1/customers/me", config);
    const user = this.remapUserFromData(data, token, true);
    this.storeUserInStore(user);

    return true;
  }

  /**
   * Checks if the user is logged into the Magento system.
   * @param user
   * @returns true if logged in, false otherwise
   */
  static async isLoggedIn(): Promise<boolean> {
    // Get the user from store, if any
    let user = this.getUser();
    // No token. We're not logged in for sure.
    if (!user.token) {
      return false;
    }
    try {
      // If we can get the customer, then we're logged in
      const { data } = await axiosCustom.get("/rest/V1/customers/me");
      user = this.remapUserFromData(data, user.token, true);
      this.storeUserInStore(user);
      return true;
    } catch (error) {
      // we're not logged in, or some error had occured. Reset user
      this.storeUserInStore(new User());
      return false;
    }
  }

  static isLoggedInQuick(): boolean {
    // Get the user from store, if any
    const user = this.getUser();
    // No token. We're not logged in for sure.
    if (!user.token) {
      return false;
    }
    return true;
  }

  /**
   * Logout the current user from the system.
   * @returns
   */
  static async logOutWithOutClearCart(): Promise<boolean> {
    // Reset user
    this.storeUserInStore(new User());
    router.push({ name: "wizard.two" });
    return true;
  }

  /**
   * Logout the current user from the system.
   * @returns
   */
  static async logOut(): Promise<boolean> {
    // Reset user
    // Get the user from store, if any
    const user = this.getUser();
    // No token. We're not logged in for sure.
    if (user.token) {
      console.log(user);
      store.state.Root.cart.clearStorage();
      this.storeCarInStore(new Cart());
    }
    this.storeUserInStore(new User());

    router.push({ name: "wizard.two" });
    return true;
  }

  /**
   * Creates new customer in the remote system.
   * @param user
   * @param password
   */

  static async create(
    user: IUser,
    password: string,
    rtoken: string
  ): Promise<boolean> {
    // Create customer in Magento
    const body = {
      customer: {
        email: user.email,
        firstname: user.firstName,
        lastname: user.lastName,
        store_id: 1,
      },
      password: password,
    };
    const data_header = {
      headers: {
        "X-ReCaptcha": rtoken,
      },
    };
    const { data } = await axiosCustom.post(
      "/rest/V1/customers",
      body,
      data_header
    );

    // Get the Magento token first

    const token = await this.token(user.email, password);
    user.id = data.id;
    user.group_id = data.group_id;
    user.website_id = data.website_id;
    user.store_id = data.store_id;
    user.loggedIn = true;
    user.token = token;
    this.storeUserInStore(user);
    // eslint-disable-next-line no-console
    console.log("createUser", user);
    // Now create the customer in WISE. Stupid, I know...
    const wiseBody = {
      entityId: data.id,
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
    };
    await axiosCustom.post("/rest/V1/WAG/wise/create/customer", wiseBody);
    return true;
  }

  static async updateUser(user: IUser): Promise<IAddress> {
    if (user.addresses.length > 0) {
      const address: IAddress = user.addresses[0];
      const body = {
        email: user.email,
        address: {
          customerId: address.customerId,
          firstname: address.firstname,
          lastname: address.lastname,
          defaultBilling: address.default_billing,
          defaultShipping: address.default_shipping,
          countryId: address.countryId,
          street: [address.street.length ? address.street[0] : ""],
          city: address.city,
          region_id: address.region.regionId,
          postcode: address.postcode,
          telephone: address.telephone,
        },
      };

      // If existing address
      if (address.id) {
        body["address"]["id"] = address.id;
      }
      const { data } = await axiosCustom.put<IAddress>(
        "/rest/V1/WAG/customer/address",
        body
      );

      // If user has added new address, persist it locally
      if (data.id) {
        address.id = data.id;
      }
      this.storeUserInStore(user);
      // eslint-disable-next-line no-console
      console.log("updateUser");
      return data;
    } else {
      throw new Error("Address is not filled");
    }
  }

  // /**
  //  * Updates user to the remote system with the new info from the billinginfo
  //  * @param user
  //  * @param billing
  //  * @returns
  //  */
  // static async updateFromBillingInfo(user: IUser, billing: BillingInfo): Promise<boolean> {
  //   let address: IAddress;
  //   if (user.addresses && user.addresses.length) {
  //     address = user.addresses[0];
  //   } else {
  //     address = {
  //       city: "",
  //       countryId: "",
  //       customerId: user.id,
  //       default_billing: true,
  //       default_shipping: true,
  //       firstname: "",
  //       id: 0,
  //       lastname: "",
  //       postcode: "",
  //       region: {
  //         region: "",
  //         regionCode: "",
  //         regionId: 0,
  //       },
  //       street: [""],
  //       telephone: "",
  //     };
  //   }

  //   address.city = billing.city.trim();
  //   address.countryId = billing.countryId;
  //   address.firstname = billing.firstname.trim();
  //   address.lastname = billing.lastname.trim();
  //   address.postcode = billing.postcode.trim();
  //   if (address.street.length) {
  //     address.street[0] = billing.address.trim();
  //   }
  //   address.postcode = billing.postcode.trim();
  //   address.customerId = user.id;
  //   // Get the region id
  //   // const { id } = await CountryService.getRegionByCountryAndState(billing.country.short, billing.state.short);
  //   // address.regionId = id;

  //   const data = await this.updateUser(user, address);

  //   // If user has added new address, persist it locally
  //   if (!user.addresses.length) {
  //     address.id = data.id;
  //     user.addresses.push(address);
  //     this.storeUserInStore(user);
  //   }

  //   return true;
  // }

  /**
   * Update address with user info
   * @param address
   * @param user
   */
  static updateAddressFromUser(address: IAddress, user: IUser) {
    address.customerId = user.id;
    address.firstname = user.firstName;
    address.lastname = user.lastName;
  }

  /**
   * Store user in the application store.
   * @param a
   */
  private static remapAddressFromData(a: any): IAddress {
    return {
      id: a.id,
      city: a.city,
      countryId: a.country_id,
      customerId: a.customer_id,
      default_billing: a.default_billing ? a.default_billing : true,
      default_shipping: false,
      firstname: a.firstname,
      lastname: a.lastname,
      postcode: a.postcode,
      region: {
        region: a.region.region_code,
        regionCode: a.region.region,
        regionId: a.region.region_id,
      },
      street: a.street,
      telephone: a.telephone,
    };
  }
  /**
   * Store user in the application store.
   * @param data
   * @param token
   * @param loggedIn
   */
  private static remapUserFromData(
    data: any,
    token: string,
    loggedIn = false
  ): IUser {
    const user = new User();
    user.firstName = data.firstName;
    user.token = token;
    user.firstName = data.firstname;
    user.lastName = data.lastname;
    user.id = data.id;
    user.loggedIn = loggedIn;
    user.email = data.email;
    user.group_id = data.group_id;
    user.website_id = data.website_id;
    user.store_id = data.store_id;
    data.addresses.forEach((a: any) => {
      const address = this.remapAddressFromData(a);
      user.addresses.push(address);
    });
    return user;
  }

  /**
   * Store user in the application store.
   * @param user
   */
  private static storeUserInStore(user: IUser): void {
    store.dispatch("Root/setUser", user);
    this.saveToLocalStorage(user);
  }

  /**
   * Store cart in the application store.
   * @param user
   */
  private static storeCarInStore(cart: Cart): void {
    store.dispatch("Root/setCart", cart);
  }

  /**
   * Get user either from store or from local storage, if persisted.
   * @returns
   */
  private static getUser(): IUser {
    // Get the user from store, if any
    let user: IUser = store.state.Root.user;
    if (!user.token) {
      // No user. Try to read it from local storage
      const persistedUser = this.readFromLocalStorage();
      if (persistedUser) {
        user = persistedUser;
        // eslint-disable-next-line no-console
        console.log("getUser");
        store.dispatch("Root/setUser", persistedUser);
      }
    }
    return user;
  }

  /**
   * Reads persisted user from the local storage.
   * @returns
   */
  private static readFromLocalStorage(): IUser | null {
    const val = Cookies.get(UserService.USER_KEY);
    if (!val) {
      return null;
    }
    const obj = JSON.parse(val);
    if (!obj) {
      return null;
    }
    const user = new User();
    for (const key in obj) {
      if (key in user) {
        user[key] = obj[key];
      }
    }
    return user;
  }

  /**
   * Save the user to local storage.
   * @param user
   */
  private static saveToLocalStorage(user: IUser): void {
    const inHour = new Date(new Date().getTime() + 60 * 60 * 1000);
    Cookies.set(UserService.USER_KEY, JSON.stringify(user), {
      expires: inHour,
    });
  }
}
